/**
 * Guest List Dialog Component - Modernized Design
 * Reusable dialog for displaying guest lists (In-House, Arrivals, Departures)
 */
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ReliabilityBadge } from '../reliability/ReliabilityBadge';
import { LogIn, LogOut, Eye, Calendar, CalendarX, Printer, Mail, Phone, MapPin, Hotel, Bed, Search, X } from 'lucide-react';

const getNightsBadgeColor = (nights) => {
  if (nights === 1) return 'bg-gradient-to-br from-rose-500/20 to-red-500/10 text-red-700 border-red-300/30';
  if (nights === 2) return 'bg-gradient-to-br from-orange-500/20 to-amber-500/10 text-orange-700 border-orange-300/30';
  if (nights <= 4) return 'bg-gradient-to-br from-blue-500/20 to-cyan-500/10 text-blue-700 border-blue-300/30';
  return 'bg-gradient-to-br from-purple-500/20 to-violet-500/10 text-purple-700 border-purple-300/30';
};

const getCategoryStyle = (title) => {
  if (title.includes('In-House')) {
    return {
      gradient: 'bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600',
      iconBg: 'bg-cyan-500',
      badgeBg: 'bg-cyan-500/10 text-cyan-700 border-cyan-300/30'
    };
  }
  if (title.includes('Arrivals')) {
    return {
      gradient: 'bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600',
      iconBg: 'bg-emerald-500',
      badgeBg: 'bg-emerald-500/10 text-emerald-700 border-emerald-300/30'
    };
  }
  if (title.includes('Departures')) {
    return {
      gradient: 'bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-600',
      iconBg: 'bg-orange-500',
      badgeBg: 'bg-orange-500/10 text-orange-700 border-orange-300/30'
    };
  }
  return {
    gradient: 'bg-gradient-to-br from-gray-500 to-gray-600',
    iconBg: 'bg-gray-500',
    badgeBg: 'bg-gray-500/10 text-gray-700 border-gray-300/30'
  };
};

export const GuestListDialog = ({
  open,
  onOpenChange,
  title,
  guests,
  reliabilityData,
  onCheckIn,
  onCheckOut,
  onViewFolio,
  onExtendStay,
  onEarlyCheckOut,
  onPrintRegCard,
  showCheckInButton = false,
  showCheckOutButton = false,
  showExtendStayButton = false,
  showEarlyCheckOutButton = false,
  showPrintRegCardButton = false
}) => {
  const categoryStyle = getCategoryStyle(title);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter guests based on search query (name or room number)
  const filteredGuests = useMemo(() => {
    if (!searchQuery.trim()) {
      return guests;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return guests.filter(guest => {
      // Search by guest name
      const nameMatch = guest.guest_name?.toLowerCase().includes(query);
      
      // Search by room number
      const roomMatch = guest.assigned_room_number?.toLowerCase().includes(query);
      
      return nameMatch || roomMatch;
    });
  }, [guests, searchQuery]);
  
  // Clear search when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);
  
  return (
    <TooltipProvider delayDuration={200}>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-6xl max-h-[90vh] overflow-hidden p-0" 
        data-testid={`guest-list-dialog-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {/* Modern Header with Gradient */}
        <div className={`${categoryStyle.gradient} text-white px-6 py-5 rounded-t-2xl`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`${categoryStyle.iconBg} p-3 rounded-xl shadow-lg`}>
                <Hotel className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-white mb-1">
                  {title}
                </DialogTitle>
                <p className="text-white/90 text-sm">
                  {filteredGuests.length} {filteredGuests.length === 1 ? 'guest' : 'guests'}
                  {searchQuery && ` (filtered from ${guests.length})`}
                </p>
              </div>
            </div>
            
            {/* Action Icons in Header */}
            <div className="flex items-center gap-6">
              {/* Printer Icon */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex flex-col items-center gap-1.5 cursor-pointer group w-20">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-14 w-14 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 backdrop-blur-sm p-2 mx-auto"
                      onClick={() => window.print()}
                      data-testid="print-guest-list-btn"
                    >
                      <img 
                        src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/kcpbyus4_image.png" 
                        alt="Print"
                        className="w-full h-full object-contain"
                      />
                    </Button>
                    <span className="text-xs font-medium text-white/90 group-hover:text-white text-center whitespace-nowrap">Print</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-semibold">Print Guest List</p>
                </TooltipContent>
              </Tooltip>
              
              {/* View/Eye Icon */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex flex-col items-center gap-1.5 cursor-pointer group w-20">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-14 w-14 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 backdrop-blur-sm p-2 mx-auto"
                      onClick={() => {/* Add view action handler */}}
                      data-testid="view-details-btn"
                    >
                      <div className="w-full h-full border-2 border-gray-700 rounded-md flex items-center justify-center">
                        <Eye className="w-5 h-5 text-gray-700" />
                      </div>
                    </Button>
                    <span className="text-xs font-medium text-white/90 group-hover:text-white text-center whitespace-nowrap">View</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-semibold">View Details</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Extend Stay Icon */}
              {showExtendStayButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer group w-20">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-14 w-14 bg-white border-2 border-green-300 hover:bg-green-50 hover:border-green-400 backdrop-blur-sm p-2 mx-auto"
                        onClick={() => {/* Extend stay would need a guest selection flow */}}
                        data-testid="extend-stay-header-btn"
                      >
                        <img 
                          src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/7w0ro2zc_ECXTENED%20THE%20STAY.png" 
                          alt="Extend Stay"
                          className="w-full h-full object-contain"
                        />
                      </Button>
                      <span className="text-xs font-medium text-white/90 group-hover:text-white text-center whitespace-nowrap">Extend Stay</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-semibold">Extend Stay</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* Check-Out Icon */}
              {showCheckOutButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer group w-20">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-14 w-14 bg-white border-2 border-red-300 hover:bg-red-50 hover:border-red-400 backdrop-blur-sm p-2 mx-auto"
                        onClick={() => {/* Check-out would need a guest selection flow */}}
                        data-testid="checkout-header-btn"
                      >
                        <img 
                          src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/cl4k4plp_Check%20out%20icon.png" 
                          alt="Check-Out"
                          className="w-full h-full object-contain"
                        />
                      </Button>
                      <span className="text-xs font-medium text-white/90 group-hover:text-white text-center whitespace-nowrap">Check-Out</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-semibold">Check-Out Guest</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {/* Early Check-Out Icon */}
              {showEarlyCheckOutButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex flex-col items-center gap-1.5 cursor-pointer group w-20">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-14 w-14 bg-white border-2 border-red-300 hover:bg-red-50 hover:border-red-400 backdrop-blur-sm p-2 mx-auto"
                        onClick={() => {/* Early check-out would need a guest selection flow */}}
                        data-testid="early-checkout-header-btn"
                      >
                        <img 
                          src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/t5mpb6jl_image.png" 
                          alt="Early Check-Out"
                          className="w-full h-full object-contain"
                        />
                      </Button>
                      <span className="text-xs font-medium text-white/90 group-hover:text-white text-center whitespace-nowrap">Early Check-Out</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-semibold">Early Check-Out</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 pt-4 pb-2 bg-gradient-to-b from-gray-50 to-white border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by guest name or room number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-12 text-base bg-white border-2 border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all"
              data-testid="guest-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Clear search"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {searchQuery && filteredGuests.length === 0 && (
            <p className="text-sm text-orange-600 mt-2 flex items-center gap-1">
              <span className="font-semibold">No results found</span> for "{searchQuery}"
            </p>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-6 py-4">
          {filteredGuests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="bg-gray-100 rounded-full p-6 mb-4">
                <Hotel className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery ? 'No Matching Guests' : 'No Guests Found'}
              </h3>
              <p className="text-gray-500 max-w-md">
                {searchQuery 
                  ? `No guests found matching "${searchQuery}". Try a different search term.`
                  : 'There are currently no guests in this category. Check back later or refresh the dashboard.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGuests.map((guest, index) => (
                <Card 
                  key={guest.id || guest.booking_id} 
                  className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-blue-300/50 bg-gradient-to-br from-white to-gray-50/30"
                  data-testid={`guest-row-${guest.id || guest.booking_id}`}
                >
                  <div className="p-5">
                    {/* Guest Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-4 flex-1">
                        {/* Guest Avatar/Initial */}
                        <div className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl shadow-lg">
                          {guest.guest_name?.charAt(0).toUpperCase() || 'G'}
                        </div>
                        
                        {/* Guest Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">
                              {guest.guest_name}
                            </h3>
                            {reliabilityData[guest.guest_email] && (
                              <ReliabilityBadge 
                                tier={reliabilityData[guest.guest_email].tier} 
                                size="sm"
                              />
                            )}
                          </div>
                          
                          {/* Contact Info */}
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                            {guest.guest_email && (
                              <div className="flex items-center gap-1.5">
                                <Mail className="w-4 h-4 text-blue-500" />
                                <span>{guest.guest_email}</span>
                              </div>
                            )}
                            {guest.guest_phone && (
                              <div className="flex items-center gap-1.5">
                                <Phone className="w-4 h-4 text-green-500" />
                                <span>{guest.guest_phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Nights Badge */}
                      <Badge 
                        className={`${getNightsBadgeColor(guest.nights)} px-4 py-2 text-sm font-semibold border-2`}
                      >
                        {guest.nights} {guest.nights === 1 ? 'night' : 'nights'}
                      </Badge>
                    </div>

                    {/* Booking Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-xl border-2 border-blue-100/50">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          <Bed className="w-4 h-4" />
                          Room Type
                        </div>
                        <div className="font-bold text-gray-900 text-base">
                          {guest.room_type || 'Not Assigned'}
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          <MapPin className="w-4 h-4" />
                          Room Number
                        </div>
                        <div className="font-bold text-blue-600 text-xl">
                          {guest.assigned_room_number || guest.room_number || 'TBD'}
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          <Calendar className="w-4 h-4" />
                          Check-In
                        </div>
                        <div className="font-semibold text-gray-900 leading-tight">
                          {new Date(guest.check_in).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          <Calendar className="w-4 h-4" />
                          Check-Out
                        </div>
                        <div className="flex items-center gap-8">
                          <div className="font-semibold text-gray-900 leading-tight">
                            {new Date(guest.check_out).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                          
                          {/* Check-Out Quick Action Button */}
                          {showCheckOutButton && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="h-11 w-11 border-2 border-red-300 hover:bg-red-50 hover:border-red-400 hover:scale-105 transition-transform p-1.5 overflow-hidden shadow-sm ml-auto flex items-center justify-center"
                                  onClick={() => onCheckOut(guest)}
                                  data-testid={`checkout-btn-${guest.id || guest.booking_id}`}
                                  aria-label="Check Out"
                                >
                                  <img 
                                    src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/cl4k4plp_Check%20out%20icon.png" 
                                    alt="Check-Out"
                                    className="w-full h-full object-contain"
                                  />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-semibold">Check Out</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          
                          {/* Early Check-Out Quick Action Button */}
                          {showEarlyCheckOutButton && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="h-11 w-11 border-2 border-red-300 hover:bg-red-50 hover:border-red-400 hover:scale-105 transition-transform p-1.5 overflow-hidden shadow-sm ml-2 flex items-center justify-center"
                                  onClick={() => onEarlyCheckOut(guest)}
                                  data-testid={`early-checkout-btn-${guest.id || guest.booking_id}`}
                                  aria-label="Early Check-Out"
                                >
                                  <img 
                                    src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/t5mpb6jl_image.png" 
                                    alt="Early Check-Out"
                                    className="w-full h-full object-contain"
                                  />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-semibold">Early Check-Out</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      
                    </div>

                    {/* Action Buttons - Icon-First Design with Tooltips */}
                    <div className="flex flex-wrap gap-2">
                      {showCheckInButton && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              className="h-10 w-10 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white shadow-md hover:shadow-lg transition-all"
                              onClick={() => onCheckIn(guest)}
                              data-testid={`checkin-btn-${guest.id || guest.booking_id}`}
                              aria-label="Check In"
                            >
                              <LogIn className="w-5 h-5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-semibold">Check In</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      
                      {showPrintRegCardButton && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-10 w-10 border-2 border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400"
                              onClick={() => onPrintRegCard(guest)}
                              data-testid={`print-regcard-btn-${guest.id || guest.booking_id}`}
                              aria-label="Print Registration Card"
                            >
                              <Printer className="w-5 h-5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-semibold">Print Registration Card</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      
                      {showExtendStayButton && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-10 w-10 border-2 border-green-300 hover:bg-green-50 hover:border-green-400 p-1.5 overflow-hidden"
                              onClick={() => onExtendStay(guest)}
                              data-testid={`extend-stay-btn-${guest.id || guest.booking_id}`}
                              aria-label="Extend Stay"
                            >
                              <img 
                                src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/7w0ro2zc_ECXTENED%20THE%20STAY.png" 
                                alt="Extend Stay"
                                className="w-full h-full object-contain"
                              />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-semibold">Extend Stay</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-10 w-10 border-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 p-2"
                            onClick={() => onViewFolio(guest)}
                            data-testid={`view-folio-btn-${guest.id || guest.booking_id}`}
                            aria-label="View Guest Folio"
                          >
                            <div className="w-full h-full border-2 border-gray-700 rounded-md flex items-center justify-center">
                              <Eye className="w-4 h-4 text-gray-700" />
                            </div>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-semibold">View Guest Folio</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </TooltipProvider>
  );
};
